import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { Button, DialogActions, DialogBody, DialogContent, DialogTitle, DialogTrigger, Field } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { IFileDto } from '../../../../config/fileConfig'
import { FilePartyDto, MainDto } from '../../../../config/filePartyConfig'
import FilePartyCombobox from '../../../../components/Combobox/FilePartyCombobox'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { DialogBodyProps } from '@fluentui/react-dialog'
import FormContainer from '../../../../components/Form/FormContainer'
import { HammockDialogProps } from '../HammockDialog'
import EmailAddressDetails = Office.EmailAddressDetails

export type ComposeOutlookItemDialogBodyProps = DialogBodyProps & {
    titleKey: string
    descriptionKey: string
    submitButtonTextKey: string

    file: IFileDto
    parties: MainDto[]
    onCompose: (recipients: EmailAddressDetails[], subject: string) => Promise<void>
    callback?: () => void
}

export const formatRecipients = (recipients: MainDto[]): EmailAddressDetails[] =>
    recipients.flatMap(
        party =>
            (party.email || '').split(/[,;]/).map(emailAddress => ({
                displayName: party.displayName,
                emailAddress: emailAddress,
            })) as EmailAddressDetails[],
    )

export const formatSubject = (file: IFileDto, recipients: MainDto[]): string => {
    const references: string[] = [file.reference, ...recipients.filter(party => !!party.fileReference).map(party => party.fileReference)]
    return `${file.name} ${[...new Set(references)].map(reference => `[${reference}]`).join(' - ')}`
}

export default function ComposeOutlookItemDialogBody<BodyPropsT extends ComposeOutlookItemDialogBodyProps>(
    props: HammockDialogProps<BodyPropsT>,
): ReactElement {
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const dialogBodyProps = useMemo(() => props.dialogBodyProps?.() ?? ({} as BodyPropsT), [props])

    const [filePartyIds, setFilePartyIds] = useState<string[]>([])

    return (
        <DialogBody>
            <DialogTitle>{translate(dialogBodyProps?.titleKey)}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    <div>{dialogBodyProps?.descriptionKey && translate(dialogBodyProps?.descriptionKey)}</div>

                    <Field label={translate('email.compose.recipient_other')} orientation={viewportSmall ? 'vertical' : 'horizontal'}>
                        <FilePartyCombobox
                            multiselect
                            entities={dialogBodyProps?.parties || []}
                            selectedOptions={filePartyIds}
                            onOptionSelect={(event, data) => setFilePartyIds(data.selectedOptions)}
                        />
                    </Field>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Button
                    appearance="primary"
                    onClick={() => {
                        const recipients: (MainDto | FilePartyDto)[] = dialogBodyProps?.parties.filter(party => filePartyIds.includes(party.pivotId))
                        dialogBodyProps
                            ?.onCompose?.(formatRecipients(recipients), formatSubject(dialogBodyProps?.file, recipients))
                            ?.then(() => setFilePartyIds([]))
                    }}
                >
                    {translate(dialogBodyProps?.submitButtonTextKey || 'action.save')}
                </Button>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">{translate('action.cancel')}</Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
